import type { ReportExport, ReportExportStatus } from '@jaf/contracts';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { isUnfinished } from '../api/report-exports-queries';
import type { DownloadOutcome } from './use-download-report-export';

/**
 * Baixa sozinho cada exportação assim que fica pronta (o arquivo não fica guardado na API) e
 * avisa na tela; a que falha gera um aviso de erro. Uma pronta que ainda não foi baixada
 * (a tela foi fechada antes) também é baixada ao abrir. Falhas antigas não geram aviso.
 */
export function useAutoDownloadReportExports(
  items: readonly ReportExport[] | undefined,
  download: (reportExport: ReportExport) => Promise<DownloadOutcome>,
): void {
  const known = useRef<Map<string, ReportExportStatus> | null>(null);
  useEffect(() => {
    if (items === undefined) return;
    const previous = known.current;
    known.current = new Map(items.map((item) => [item.id, item.status]));
    for (const item of items) {
      if (item.status === 'READY') {
        void download(item).then((outcome) => {
          if (outcome === 'SAVED') {
            toast.success('Relatório pronto e baixado', { description: item.fileName });
          }
        });
        continue;
      }
      const before = previous?.get(item.id);
      if (item.status === 'FAILED' && before !== undefined && isUnfinished({ status: before })) {
        toast.error(item.failureMessage ?? 'Não foi possível gerar o arquivo', {
          description: item.fileName,
        });
      }
    }
  }, [items, download]);
}
